import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useGenerateLessonQuizMutation,
  useLazyGetLessonQuizQuery,
} from "../../services/users/ai";
import { useAiPoll } from "../../hooks/useAiPoll";
import { toastAiError } from "../../utils/aiErrors";

const AiQuizPanel = ({ lessonId, transcriptReady }) => {
  const { t } = useTranslation("ai");
  const [getQuiz] = useLazyGetLessonQuizQuery();
  const [generateQuiz, { isLoading: isGenerating }] =
    useGenerateLessonQuizMutation();

  const [status, setStatus] = useState("loading");
  const [questions, setQuestions] = useState([]);
  const [shouldPoll, setShouldPoll] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [index, setIndex] = useState(0);

  const applyData = useCallback((data) => {
    if (data?.status === "completed" && Array.isArray(data.questions)) {
      setStatus("completed");
      setQuestions(data.questions);
      setShouldPoll(false);
      setAnswers({});
      setSubmitted(false);
      setIndex(0);
    } else if (data?.status === "pending") {
      setStatus("pending");
      setShouldPoll(true);
    } else {
      setStatus("not_generated");
    }
  }, []);

  const load = useCallback(async () => {
    setStatus("loading");
    const result = await getQuiz(lessonId);
    if (result.error) {
      if (result.error.status === 404) {
        setStatus("not_generated");
        setQuestions([]);
        return;
      }
      toastAiError(result.error, t);
      setStatus("not_generated");
      return;
    }
    applyData(result.data);
  }, [applyData, getQuiz, lessonId, t]);

  useEffect(() => {
    setQuestions([]);
    setAnswers({});
    setSubmitted(false);
    setIndex(0);
    setShouldPoll(false);
    load();
  }, [lessonId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchForPoll = useCallback(async () => {
    const result = await getQuiz(lessonId);
    if (result.error) {
      if (result.error.status === 404) return { status: "pending" };
      throw result.error;
    }
    return result.data;
  }, [getQuiz, lessonId]);

  const { isPolling, timedOut, start: startPoll } = useAiPoll(fetchForPoll, {
    enabled: shouldPoll,
    onCompleted: applyData,
    onError: (err) => {
      setShouldPoll(false);
      toastAiError(err, t);
      setStatus("not_generated");
    },
    onTimeout: () => setShouldPoll(false),
  });

  const handleGenerate = async () => {
    try {
      const result = await generateQuiz({ lessonId, question_count: 5 });
      if (result.error) {
        toastAiError(result.error, t);
        return;
      }
      setStatus("pending");
      setShouldPoll(true);
      startPoll();
    } catch (err) {
      toastAiError(err, t);
    }
  };

  const score = questions.reduce((acc, q, i) => {
    const selected = answers[i];
    if (selected == null) return acc;
    const correct = q.options?.findIndex((o) => o.is_correct);
    return acc + (selected === correct ? 1 : 0);
  }, 0);

  if (status === "loading") {
    return <p className="ai-muted">{t("common.loading")}</p>;
  }

  if (status === "pending" || isPolling || isGenerating) {
    return (
      <div className="ai-status-block">
        <p>{t("quiz.generating")}</p>
        <p className="ai-muted">{t("common.polling")}</p>
        {timedOut && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={startPoll}>
            {t("quiz.retry")}
          </button>
        )}
      </div>
    );
  }

  if (status !== "completed" || !questions.length) {
    return (
      <div className="ai-status-block">
        <p>{transcriptReady ? t("quiz.empty") : t("quiz.needTranscript")}</p>
        <p className="ai-muted">{t("quiz.disclaimer")}</p>
        <button
          type="button"
          className="btn btn-main-color font-15"
          onClick={handleGenerate}
          disabled={!transcriptReady || isGenerating}
        >
          {t("quiz.generate")}
        </button>
      </div>
    );
  }

  const q = questions[index];
  const selected = answers[index];
  const isFirst = index === 0;
  const isLast = index >= questions.length - 1;
  const allAnswered = questions.every((_, i) => answers[i] != null);

  const handleCheckAnswers = () => {
    if (!allAnswered) {
      toast.warning(t("quiz.answerAllFirst"));
      return;
    }
    setSubmitted(true);
    setIndex(0);
  };

  return (
    <div className="ai-quiz">
      <p className="ai-muted">{t("quiz.disclaimer")}</p>

      <div className="ai-quiz__meta">
        <span>
          {index + 1} / {questions.length}
        </span>
        {submitted && (
          <strong>{t("quiz.score", { correct: score, total: questions.length })}</strong>
        )}
      </div>

      <div className="ai-quiz__question">
        <h3 className="font_16 font-bold">{q.question}</h3>
        <div className="ai-quiz__options">
          {q.options?.map((opt, oi) => {
            let className = "ai-quiz__option";
            if (submitted) {
              if (opt.is_correct) className += " is-correct";
              else if (selected === oi) className += " is-wrong";
            } else if (selected === oi) {
              className += " is-selected";
            }
            return (
              <button
                key={oi}
                type="button"
                className={className}
                disabled={submitted}
                onClick={() =>
                  setAnswers((prev) => ({ ...prev, [index]: oi }))
                }
              >
                {opt.text}
              </button>
            );
          })}
        </div>

        {submitted && q.explanation && (
          <div className="ai-quiz__explanation">
            <strong>{t("quiz.explanation")}</strong>
            <p className="mb-0">{q.explanation}</p>
          </div>
        )}
      </div>

      <div className="ai-quiz__nav">
        <button
          type="button"
          className="btn ai-quiz__nav-btn"
          disabled={isFirst}
          onClick={() => setIndex((i) => i - 1)}
        >
          {t("quiz.prev")}
        </button>

        {!submitted && isLast && (
          <button
            type="button"
            className={`btn btn-main-color font-15 ${
              allAnswered ? "" : "ai-quiz__submit--inactive"
            }`}
            onClick={handleCheckAnswers}
          >
            {t("quiz.submit")}
          </button>
        )}

        {submitted && (
          <button
            type="button"
            className="btn btn-main-color-border font-15"
            onClick={handleGenerate}
          >
            {t("quiz.regenerate")}
          </button>
        )}

        <button
          type="button"
          className="btn ai-quiz__nav-btn"
          disabled={isLast}
          onClick={() => setIndex((i) => i + 1)}
        >
          {t("quiz.next")}
        </button>
      </div>
    </div>
  );
};

export default AiQuizPanel;
